import { Line} from 'react-chartjs-2';
import React, { useCallback, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { binDBKeys } from 'src/pages/binOverview/BinCommander';
import { useBinStateFromAzure } from 'src/queries/useBinStateFromAzure';
import { getUserTimezoneOffset } from '../../../components/BinDetails';
import { DatePicker, Divider, Skeleton } from 'antd';
import BinApiService from 'src/api/BinApiService';
import { BaseChartProps } from './TemperatureCableChart';
import { queryClient } from 'src';
import { ChartDatePicker } from './Co2Chart';
import { RangeValue } from './ChartContainer';
import { FanControlMoisturePreferenceProps, tooltipItemRound, useFanHeaterChangeHistory } from './shared';
import { ChartDataset, Chart as ChartJS} from 'chart.js';
// @ts-ignore
import crosshair from '@adelamar/chartjs-plugin-crosshair';
import { useModeChangeHistory } from "./modehistoryAndFanChangeHistory";
import annotationPlugin from 'chartjs-plugin-annotation';
import {annotationFixOverlapPlugin} from 'src/pages/dashboard/charts/plugins/annotationOverlapFixPlugin';
import { queryOptions } from 'src/queries/v4QueryOptions/queryOptions';
import { BaseChartQueryOptions } from './modehistoryAndFanChangeHistory';
ChartJS.register(crosshair);
ChartJS.register(annotationPlugin);
ChartJS.register(annotationFixOverlapPlugin);
// @ts-ignore
ChartJS.defaults.plugins.crosshair.zoom = {enabled: false};

ChartJS.defaults.animation = false;
ChartJS.defaults.responsive = true;
ChartJS.defaults.maintainAspectRatio = false;
ChartJS.defaults.datasets.line.pointRadius = 0;

export const ambientFanDatasetColor = "rgb(235,124,52)";
export const plenumFanDatasetColor = "rgb(66,113,192)";


export const plenumDewPointColor = 'rgba(254,106,138)';

export const useGetLowestLayerTemperatureHistoryQueryOptions = (options: BaseChartQueryOptions) => {
  const queryClient = useQueryClient();
  return queryOptions({
    queryKey: [...binDBKeys.bin(options.binId), "charts", "LowestLayer", { startDate: options.range?.[0], endDate: options.range?.[1] }], staleTime: Infinity, retry: 0, keepPreviousData: true,
    queryFn: async (q) => {
      const results = await BinApiService.getLowestLayerTemperatureHistory(options.binId, options.range?.[0]?.toISOString()!, options.range?.[1]?.toISOString()!, null, q.signal);
      queryClient.removeQueries({queryKey: q.queryKey.slice(0, -1), type: "inactive"});

      return results;
    }, enabled: (options.binId ?? 0) > 0 && options.range?.[0] != null && options.range?.[1] != null
  });
}

export const LayerDewPointChart = (props: BaseChartProps & {showTitle?: boolean, yTitle?: string}) => {

  const currentDate = dayjs();
  const priorDate = currentDate.subtract(14, 'days');


  const isControlled = typeof props.value != 'undefined';
  const [internalValue, setIntervalValue] = useState<RangeValue>([priorDate, currentDate]);

  // Internally, we need to deal with some value. Depending on whether
	// the component is controlled or not, that value comes from its
	// props or from its internal state.

	const value = isControlled ? props.value : internalValue;

  const onDateChange = useCallback((values: RangeValue, formatter: any) => {
    if (props.onChange) {
      props.onChange(values);
    }
    else {
      setIntervalValue(values);
    }
  }, [props.onChange]);

  const getAmbientPlenumHistoryQueryOptions = useAmbientPlenumTemperatureQueryOptions({
    binId: props.binId,
    range: value,
  });

  const chartQuery = useQuery({...getAmbientPlenumHistoryQueryOptions});

  const getLowestLayerTemperatureHistoryQueryOptions = useGetLowestLayerTemperatureHistoryQueryOptions({
    binId: props.binId,
    range: value,
  })
  
  const chartLowestLayerQuery = useQuery({...getLowestLayerTemperatureHistoryQueryOptions});

  const modeHistory = useModeChangeHistory(props.binId, value?.[0], value?.[1]);
  const fanHeaterChangeHistory = useFanHeaterChangeHistory(props.binId, value?.[0], value?.[1]);



  return <div style={{width: "100%", height: props.chartHeight ?? "320px", display: "flex", justifyContent: "center" }}>

{chartQuery.data && <Line options={{
        responsive: true,
        animation: false,
        spanGaps: false,
        maintainAspectRatio: false,
        datasets: {
          line: {
            pointRadius: 0 // disable for all `'line'` datasets
          }
        },
        scales: {
          x: {
            type: "time",
            time: {
              minUnit: 'day',
              displayFormats: {
                //day: 'l'
              }
            },
          },
          y: {
            suggestedMin: 32,
            suggestedMax: 80,
            title: {
              font: {
                size: 16,
              },
              display: true,
              text: props.yTitle ?? "Temperature °F",
            }
          }
        },
        plugins: {
          annotation: {
            annotations: [
              ...modeHistory.annotations,
              ...fanHeaterChangeHistory.annotations,
            ],
          },
          legend: {
            position: 'top' as const,
            display: true,
          },
          title: {
            display: props.showTitle ?? true,
            text: 'Incoming Air Dew Point',
          },

          tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: {
              label: tooltipItemRound,
            },
          },
          autocolors: {
            enabled: false,
          },
// @ts-ignore
sync: {
            enabled: true,
            group: 1,
            suppressTooltips: false,   // suppress tooltips when showing a synced tracer
          },
        },
      }} data={{
        // @ts-ignore
        datasets: [
          {
            data: chartQuery.data?.[3]?.data ?? [],
            label: "Plenum Dew Point",
            borderColor: plenumFanDatasetColor,
            backgroundColor: plenumFanDatasetColor,
            
          },  
          {
          //@ts-ignore different DTO types
          data: chartLowestLayerQuery.data?.[0]?.data ?? [],
          label: "Coldest Layer Temp",
          borderColor: plenumDewPointColor,
          backgroundColor: plenumDewPointColor,
          parsing: {
            yAxisKey: "temperatureF",
          }
        },
      ],
      }} />}
      {chartQuery.data == null && <Skeleton loading />}
    </div>
}

export const useAmbientPlenumTemperatureQueryOptions = (options: BaseChartQueryOptions) => {
  const queryClient = useQueryClient();
  return queryOptions({
    queryKey: [...binDBKeys.bin(options.binId), "charts", "Ambient_Vs_Plenum", { startDate: options.range?.[0], endDate: options.range?.[1] }], staleTime: Infinity, retry: 0, keepPreviousData: true, queryFn: async (q) => {
      const results = await BinApiService.getAmbientPlenumHistory(options.binId, options.range?.[0]?.toISOString()!, options.range?.[1]?.toISOString()!, null, q.signal);
      queryClient.removeQueries({queryKey: q.queryKey.slice(0, -1), type: "inactive"});

      return results;
    }, enabled: (options.binId ?? 0) > 0 && options.range?.[0] != null && options.range?.[1] != null
  });
}

export const AmbientPlenumChart = (props: BaseChartProps & {showTitle?: boolean} & {showRecommendation?: boolean, showTemperatureChart?: boolean} & FanControlMoisturePreferenceProps) => {
  const currentDate = dayjs();
  const priorDate = currentDate.subtract(14, 'days');


  const isControlled = typeof props.value != 'undefined';
  const [internalValue, setIntervalValue] = useState<RangeValue>([priorDate, currentDate]);

  // Internally, we need to deal with some value. Depending on whether
	// the component is controlled or not, that value comes from its
	// props or from its internal state.

	const value = isControlled ? props.value : internalValue;

  const onDateChange = useCallback((values: RangeValue, formatter: any) => {
    if (props.onChange) {
      props.onChange(values);
    }
    else {
      setIntervalValue(values);
    }
  }, [props.onChange]);

  const ambientVsPlenumChartQueryOptions = useAmbientPlenumTemperatureQueryOptions({
    binId: props.binId,
    range: value,
  })

  const chartQuery = useQuery({...ambientVsPlenumChartQueryOptions});

  const modeHistory = useModeChangeHistory(props.binId, value?.[0]!, value?.[1]!);
  const fanHeaterChangeHistory = useFanHeaterChangeHistory(props.binId, value?.[0], value?.[1]);

  const formatMoistureTitle = () => {
    if (props.useRHForFanControl === false) {
      return "Incoming Air EMC";
    }
    else {
      return "Incoming Air RH";
    } 
  }



  const datasets:  Array<ChartDataset> = [];
  if (chartQuery.data) {
  datasets.push(
// @ts-ignore
{
        ...chartQuery.data[0],
        label: chartQuery.data[0]?.label!,
        borderColor: plenumFanDatasetColor,
        backgroundColor: plenumFanDatasetColor,
      });
  if (props.showRecommendation ?? true) {
// @ts-ignore
datasets.push(
// @ts-ignore
{
// @ts-ignore
...chartQuery.data[1],
        label: chartQuery.data[1]?.label!,
        borderColor: "rgb(165,164,164)",
        backgroundColor: "rgb(165,164,164)",
      }
    );    
  }
datasets.push(
// @ts-ignore
{
    ...chartQuery.data[2],
    label: chartQuery.data[2]?.label!,
    borderColor: ambientFanDatasetColor,
    backgroundColor: ambientFanDatasetColor,
  }
);
  }
return (<div style={{minHeight: props.containerMinHeight ?? "800px"}}>

    {!isControlled && <ChartDatePicker startDate={value?.[0]!} endDate={value?.[1]!} onDateRangeChange={onDateChange} />}
    <div style={{width: "100%", height: props.chartHeight ?? "320px", display: "flex", justifyContent: "center" }}>
      {chartQuery.data && <Line
       options={{
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        spanGaps: false,
        datasets: {
          line: {
            pointRadius: 0 // disable for all `'line'` datasets
          }
        },
        scales: {
          x: {
            type: "time",
            time: {
              minUnit: 'day',
              displayFormats: {
                //day: 'l'
              }
            },
          },
          y: {
            min: props.min ?? 0,
            max: props.max ?? undefined,
            suggestedMax: 24,
            ticks: {
              precision: 0,
              stepSize: 1,
            },
            title: {
              font: {
                size: 16,
              },
              display: true,
              text: (chartQuery.data?.[props.useRHForFanControl === false ? 6 : 4]?.unitLabel)!,
            }
          }
        },
        plugins: {
          annotation: {
            annotations: [
              ...modeHistory.annotations,
              ...fanHeaterChangeHistory.annotations,
            ],
          },
          legend: {
            position: 'top' as const,
            display: true,
          },
          title: {
            display: props.showTitle ?? true,
            text: formatMoistureTitle(),
          },

          tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: {
              label: tooltipItemRound,
            },
          },
          autocolors: {
            enabled: false,
          },
// @ts-ignore
sync: {
            enabled: true,
            group: 1,
            suppressTooltips: false,   // suppress tooltips when showing a synced tracer
          },
        },
      }} data={{
        datasets: [
          {
            ...chartQuery.data?.[props.useRHForFanControl === false ? 6 : 4],
            label: chartQuery.data[props.useRHForFanControl === false ? 6 : 4]?.label!,
            borderColor: "rgb(66,113,192)",
            backgroundColor: "rgb(66,113,192)",
          },
          {
            ...chartQuery.data[props.useRHForFanControl === false ? 10 : 7],
            label: chartQuery.data[props.useRHForFanControl === false ? 10 : 7]?.label!,
            borderColor: "rgb(235,124,52)",
            backgroundColor: "rgb(235,124,52)",
          },
        ]
      }} />}
      {chartQuery.data == null && <Skeleton loading />}
    </div>

{(props.showTemperatureChart ?? true) && <>

    <div style={{width: "100%", height: props.chartHeight ?? "320px", display: "flex", justifyContent: "center" }}>

      {chartQuery.data && <Line options={{
        responsive: true,
        animation: false,
        spanGaps: false,
        maintainAspectRatio: false,
        datasets: {
          line: {
            pointRadius: 0 // disable for all `'line'` datasets
          }
        },
        scales: {
          x: {
            type: "time",
            time: {
              minUnit: 'day',
              displayFormats: {
                //day: 'l'
              }
            },
          },
          y: {
            suggestedMin: 0,
            suggestedMax: 80,
            title: {
              font: {
                size: 16,
              },
              display: true,
              //text: "Temperature °F",
              text: (chartQuery.data?.[0]?.unitLabel)!,
            }
          }
        },
        plugins: {
          annotation: {
            annotations: [
              ...modeHistory.annotations,
              ...fanHeaterChangeHistory.annotations,
            ],
          },
          legend: {
            position: 'top' as const,
            display: true,
          },
          title: {
            display: props.showTitle ?? true,
            text: 'Incoming Air Temp',
          },

          tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: {
              label: tooltipItemRound,
            },
          },
          autocolors: {
            enabled: false,
          },
// @ts-ignore
sync: {
            enabled: true,
            group: 1,
            suppressTooltips: false,   // suppress tooltips when showing a synced tracer
          },
        },
      }} data={{
        // @ts-ignore
        datasets: datasets,
      }} />}
      {chartQuery.data == null && <Skeleton loading />}
    </div>
    </>}

  </div>);

};
